import React, { useEffect, useRef, useState } from "react";

import { Controller } from "react-hook-form";
import ErrorTextWithIcon from "../../Typography/ErrorTextWithIcon/ErrorTextWithIcon";
import {
    ADDRESS_FIELD,
    AE,
    ENTER_KEY,
    GEOMETRY_FIELD,
    GOOGLE_PLACE_FORMATED_ADDRESS,
    GOOGLE_PLACE_SRC,
    NAME_FIELD,
    PLACE_CHANGED,
    SA,
    UNDEFINED,
} from "./constants";
import { GoogleMapsIcon } from "@/public/Theme";

const FloatingAddressAutoCompleteField = ({
    field,
    errors,
    setError,
    setValue,
    disabled,
    label,
    control,
    defaultValue,
    searchCountry,
    icon,
    setAutCompleteAddress = () => { },
}) => {
    let autoComplete;

    const autoCompleteRef = useRef(null);

    const handleAutoComplete = () => {
        try {
            const place = autoComplete.getPlace();
            if (place?.address_components) {
                setValue(field, autoCompleteRef.current.value);
                setAutCompleteAddress(GOOGLE_PLACE_FORMATED_ADDRESS(place));
            }
        } catch (e) {
            console.error(e);
        }
    };

    useEffect(() => {
        const initializeGooglePlace = () => {
            if (typeof window !== UNDEFINED && window.google?.maps?.places) {
                try {
                    autoComplete = new window.google.maps.places.Autocomplete(
                        autoCompleteRef.current,
                        {
                            componentRestrictions: {
                                country: searchCountry?.toLowerCase(),
                            },
                            fields: [ADDRESS_FIELD, GEOMETRY_FIELD, NAME_FIELD],
                        }
                    );

                    autoComplete.addListener(PLACE_CHANGED, function () {
                        handleAutoComplete();
                    });
                } catch (e) {
                    console.error(
                        "Error initializing Google Places Autocomplete:",
                        e
                    );
                }
            } else {
                setTimeout(initializeGooglePlace, 100); // Retry after a short delay
            }
        };

        // Check if the script is already loaded
        if (document.querySelector(`script[src="${GOOGLE_PLACE_SRC}"]`)) {
            initializeGooglePlace();
        } else {
            // Dynamically load the Tabby script
            const script = document.createElement("script");
            script.src = GOOGLE_PLACE_SRC;
            script.async = true;
            script.onload = initializeGooglePlace;
            script.onerror = () => {
                console.error("Failed to load Google Places API script.");
            };
            document.body.appendChild(script);
        }
    }, [searchCountry]);

    useEffect(() => {
        if (typeof window !== UNDEFINED) {
            autoCompleteRef.current.value = defaultValue;
        }
    }, [defaultValue]);

    return (
        <div className="flex-1">
            <div className="relative w-full">
                <Controller
                    control={control}
                    name={field}
                    rules={{
                        required: true,
                    }}
                    render={({ field: { onChange, onBlur, value, ref } }) => (
                        <>
                            <input
                                ref={(e) => {
                                    ref(e);
                                    autoCompleteRef.current = e;
                                }}
                                onBlur={onBlur}
                                onChange={onChange}
                                value={value}
                                placeholder=""
                                onKeyPress={(e) => {
                                    e.key === ENTER_KEY && e.preventDefault();
                                }}
                                className={`w-full h-12 pb-2.5 px-4 pt-6 pr-10  ${disabled ? "bg-Ghost_White" : "bg-white"} rounded-[1px] outline-1 outline-offset-[-1px] outline ${disabled ? "outline-Light_Lavender" : "outline-[#ece9ee]"} focus:outline-neutral-800 appearance-none peer font-Poppins text-sm font-light ${disabled ? "text-Quick_Silver" : "text-[#262626]"} leading-[21px]`}
                            />

                            {/* ✅ Icon positioned absolutely inside input */}
                            <img
                                src={GoogleMapsIcon}
                                alt="icon"
                                className="absolute right-3 top-1/2 transform -translate-y-1/2 w-7 h-7 pointer-events-none"
                            />
                        </>
                    )}
                />

                <label
                    htmlFor={field}
                    className={`absolute left-4 text-sm font-Poppins font-light text-[#666666] leading-[21px] transition-all duration-300 
    peer-focus:text-[10px] peer-focus:sm:text-[12px] peer-focus:font-normal peer-focus:text-[#333333] peer-focus:leading-[15px] peer-focus:top-[3px]
    peer-[&:not(:placeholder-shown)]:text-[10px] peer-[&:not(:placeholder-shown)]:top-[3px] peer-[&:not(:placeholder-shown)]:font-normal ${disabled ? "peer-[&:not(:placeholder-shown)]:text-Quick_Silver" : "peer-[&:not(:placeholder-shown)]:text-[#333333]"} peer-[&:not(:placeholder-shown)]:leading-[15px]
    peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2
    peer-focus:translate-y-0`}
                >
                    {label}
                </label>
            </div>

            {errors?.[field]?.message && (
                <ErrorTextWithIcon text={errors?.[field]?.message} />
            )}
        </div>
    );
};

export default FloatingAddressAutoCompleteField;
